import { Component, OnInit, signal } from '@angular/core';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { ApiEnvironmentDeltaService, DeltaSection, EnvAreas, EnvAreasDetailModel, EnvDeltaOrganizationModel, EnvironmentDeltaFullResponse, EnvironmentDeltaResponse, SurveyDelta, AlertDelta } from '../../../../shared/services/api-envdelta.service';
import { Environments } from '../../../../shared/models/environments.type';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { InfoTooltipComponent } from '../../../../shared/components/ui/info-tooltip/info-tooltip.component';

export type EnvDeltaViewMode = 'full' | 'organization';

export interface PropDiffRow {
  key: string;
  fromVal: string | null;
  toVal: string | null;
  status: 'added' | 'removed' | 'modified' | 'same';
}

export interface TextDiffSegment {
  text: string;
  changed: boolean;
}

export interface TextDiff {
  fromSegments: TextDiffSegment[];
  toSegments: TextDiffSegment[];
}

@Component({
  selector: 'app-environment-delta',
  templateUrl: './environment-delta.component.html',
  styleUrls: ['./environment-delta.component.css'],
  imports: [CommonModule, FormsModule, BreadcrumbComponent, InfoTooltipComponent]
})
export class EnvironmentDeltaComponent implements OnInit {
  loading = signal<boolean>(false);
  error = signal<string | null>(null);

  sourceEnv: Environments = 'dev';
  targetEnv: Environments = 'eu';
  readonly areaLabels: Record<EnvAreas, string> = { organization: 'Organization', surveys: 'Surveys', alerts: 'Alerts' };

  // Delta only ever makes sense one hop down the environment chain, or the full dev-to-eu span.
  readonly directionOptions: { source: Environments; target: Environments }[] = [
    { source: 'dev', target: 'qa' },
    { source: 'qa', target: 'eu' },
    { source: 'dev', target: 'eu' },
  ];

  viewMode: EnvDeltaViewMode = 'organization';
  onlyWithDelta = false;

  deltaResult: EnvironmentDeltaResponse | null = null;
  fullDeltaResult: EnvironmentDeltaFullResponse | null = null;
  expandedChanges = new Set<string>();
  organizationId: string = '';

  constructor(
    public organizationStore: OrganizationStoreService,
    private environmentDeltaService: ApiEnvironmentDeltaService) { }

  ngOnInit() {
    this.organizationId = this.organizationStore.selectedOrganizationId() ?? '';
    this.run();
  }

  get canCompare(): boolean {
    return !!this.sourceEnv && !!this.targetEnv && this.sourceEnv !== this.targetEnv;
  }

  directionKey(source: Environments, target: Environments): string {
    return `${source}-${target}`;
  }

  get selectedDirectionKey(): string {
    return this.directionKey(this.sourceEnv, this.targetEnv);
  }

  onDirectionChange(key: string): void {
    const [source, target] = key.split('-') as [Environments, Environments];
    this.sourceEnv = source;
    this.targetEnv = target;
  }

  get canRun(): boolean {
    return this.canCompare && (this.viewMode === 'full' || !!this.organizationId);
  }

  setViewMode(mode: EnvDeltaViewMode): void {
    if (this.viewMode === mode || this.loading()) return;
    this.viewMode = mode;
    this.error.set(null);

    if (mode === 'full' && !this.fullDeltaResult) {
      this.loadFullDelta();
    } else if (mode === 'organization' && !this.deltaResult && this.organizationId) {
      this.compare();
    }
  }

  run(): void {
    if (this.viewMode === 'full') {
      this.loadFullDelta();
    } else {
      this.compare();
    }
  }

  viewOrganizationDetail(orgId: string): void {
    this.organizationId = orgId;
    this.viewMode = 'organization';
    this.deltaResult = null;
    this.compare();
  }

  loadFullDelta(): void {
    if (!this.canCompare) return;
    this.fullDeltaResult = null;
    this.error.set(null);
    this.loading.set(true);
    this.environmentDeltaService.getEnvironmentsDeltaFull(this.sourceEnv, this.targetEnv).subscribe({
      next: (result: EnvironmentDeltaFullResponse) => {
        this.fullDeltaResult = result;
        this.loading.set(false);
      },
      error: (err: any) => {
        this.error.set('Failed to load environment delta: ' + (err?.message ?? err));
        this.loading.set(false);
      }
    });
  }

  compare(): void {
    if (!this.canCompare || !this.organizationId) return;
    this.deltaResult = null;
    this.expandedChanges.clear();
    this.error.set(null);
    this.loading.set(true);
    this.environmentDeltaService.getEnvironmentsDelta(this.organizationId, this.sourceEnv, this.targetEnv).subscribe({
      next: (result: EnvironmentDeltaResponse) => {
        this.deltaResult = result;
        this.loading.set(false);
      },
      error: (err: any) => {
        this.error.set('Failed to load delta: ' + (err?.message ?? err));
        this.loading.set(false);
      }
    });
  }

  get filteredOrganizations(): EnvDeltaOrganizationModel[] {
    const orgs = this.fullDeltaResult?.organizations ?? [];
    return this.onlyWithDelta ? orgs.filter(o => o.hasDelta) : orgs;
  }

  getAreaDetail(org: EnvDeltaOrganizationModel, areaKey: EnvAreas): EnvAreasDetailModel | undefined {
    return org.areas?.find(a => a.areaKey === areaKey);
  }

  toggleChange(id: string): void {
    if (this.expandedChanges.has(id)) this.expandedChanges.delete(id);
    else this.expandedChanges.add(id);
  }

  isExpanded(id: string): boolean {
    return this.expandedChanges.has(id);
  }

  isPlainObject(str: string): boolean {
    try {
      const v = JSON.parse(str);
      return typeof v === 'object' && v !== null && !Array.isArray(v);
    } catch { return false; }
  }

  computePropertyDiff(fromStr: string, toStr: string): PropDiffRow[] {
    try {
      const fromObj: Record<string, any> = JSON.parse(fromStr);
      const toObj: Record<string, any> = JSON.parse(toStr);
      const allKeys = new Set([...Object.keys(fromObj), ...Object.keys(toObj)]);
      return Array.from(allKeys).map(key => {
        const hasFrom = key in fromObj;
        const hasTo = key in toObj;
        const fromVal = hasFrom ? String(fromObj[key]) : null;
        const toVal = hasTo ? String(toObj[key]) : null;
        let status: PropDiffRow['status'];
        if (!hasFrom) status = 'added';
        else if (!hasTo) status = 'removed';
        else if (fromVal !== toVal) status = 'modified';
        else status = 'same';
        return { key, fromVal, toVal, status };
      });
    } catch { return []; }
  }

  countSame(rows: PropDiffRow[]): number {
    return rows.filter(r => r.status === 'same').length;
  }

  formatJson(str: string): string {
    try { return JSON.stringify(JSON.parse(str), null, 2); }
    catch { return str; }
  }

  computeTextDiff(fromStr: string, toStr: string): TextDiff {
    const fromTokens = this.tokenize(this.formatJson(fromStr));
    const toTokens = this.tokenize(this.formatJson(toStr));
    const n = fromTokens.length;
    const m = toTokens.length;

    const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        lcs[i][j] = fromTokens[i] === toTokens[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }

    const fromSegments: TextDiffSegment[] = [];
    const toSegments: TextDiffSegment[] = [];
    let i = 0, j = 0;
    while (i < n && j < m) {
      if (fromTokens[i] === toTokens[j]) {
        fromSegments.push({ text: fromTokens[i], changed: false });
        toSegments.push({ text: toTokens[j], changed: false });
        i++; j++;
      } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
        fromSegments.push({ text: fromTokens[i], changed: true });
        i++;
      } else {
        toSegments.push({ text: toTokens[j], changed: true });
        j++;
      }
    }
    while (i < n) { fromSegments.push({ text: fromTokens[i], changed: true }); i++; }
    while (j < m) { toSegments.push({ text: toTokens[j], changed: true }); j++; }

    return { fromSegments: this.mergeSegments(fromSegments), toSegments: this.mergeSegments(toSegments) };
  }

  private tokenize(str: string): string[] {
    return str.match(/\w+|[^\w\s]|\s+/g) ?? [];
  }

  private mergeSegments(segments: TextDiffSegment[]): TextDiffSegment[] {
    const merged: TextDiffSegment[] = [];
    for (const seg of segments) {
      const last = merged[merged.length - 1];
      if (last && last.changed === seg.changed) last.text += seg.text;
      else merged.push({ ...seg });
    }
    return merged;
  }

  formatSectionName(section: string): string {
    return section.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());
  }

  totalOrgChanges(): number {
    return this.totalChanges(this.deltaResult?.organization?.sections);
  }

  totalChanges(sections: DeltaSection[] | undefined): number {
    return sections?.reduce((acc, s) => acc + s.changes.length, 0) ?? 0;
  }

  get surveysOnlyInSource(): SurveyDelta[] {
    return this.deltaResult?.surveys?.filter(s => s.status === 'onlyInSource') ?? [];
  }
  get surveysOnlyInTarget(): SurveyDelta[] {
    return this.deltaResult?.surveys?.filter(s => s.status === 'onlyInTarget') ?? [];
  }
  get surveysModified(): SurveyDelta[] {
    return this.deltaResult?.surveys?.filter(s => s.status === 'compared' && s.hasDelta) ?? [];
  }
  get surveysIdentical(): SurveyDelta[] {
    return this.deltaResult?.surveys?.filter(s => s.status === 'compared' && !s.hasDelta) ?? [];
  }

  get alertsOnlyInSource(): AlertDelta[] {
    return this.deltaResult?.alerts?.filter(a => a.status === 'onlyInSource') ?? [];
  }
  get alertsOnlyInTarget(): AlertDelta[] {
    return this.deltaResult?.alerts?.filter(a => a.status === 'onlyInTarget') ?? [];
  }
  get alertsModified(): AlertDelta[] {
    return this.deltaResult?.alerts?.filter(a => a.status === 'compared' && a.hasDelta) ?? [];
  }
  get alertsIdentical(): AlertDelta[] {
    return this.deltaResult?.alerts?.filter(a => a.status === 'compared' && !a.hasDelta) ?? [];
  }
}
